/**
 * The board shell — prototype H's composition: Direction strip on top, the
 * Spotlight stage left, the 440px fold column right. Plus the two content
 * states of the board proper: the compose hostage and Mode-on stripped.
 *
 * The day arc's resting faces (22:00 invite, 01:00 night) are deliberately
 * *not* here — they render in their own overlay (`render/arc.ts`) so their
 * long fades survive this function replacing `#root.innerHTML`.
 */

import type { BoardState } from '../types';
import type { UiState } from '../store';
import { computeSpotlight, type Spotlight } from '../spotlight';
import { directionViews, renderStrip } from './strip';
import { renderStage, renderModeButton } from './stage';
import { renderFolds } from './folds';
import { renderCompose } from './compose';
import { checkbox, elapsed, esc } from './util';

export interface RenderOptions {
  state: BoardState | null;
  ui: UiState;
  now: Date;
  /** false on the phone cut — composing is a sit-down act. */
  canCompose: boolean;
}

export function renderBoard(o: RenderOptions): string {
  if (!o.state) {
    return `<div class="booting"><div class="k">Life OS</div><div class="what">Reading the board…</div></div>${overlays(o)}`;
  }
  const board = o.state.data;
  const stateData = board.state.data;
  const modeOn = stateData?.mode.isOn ?? false;

  const spotlight = computeSpotlight({
    today: board.today,
    now: o.now,
    plan: stateData?.plan ?? null,
    events: board.calendar.data.events ?? [],
    directions: board.defs.data.directions,
    goals: board.defs.data.goals,
    reviews: stateData?.reviews ?? null,
    shelfCounts: {
      events: (board.calendar.data.events ?? []).length,
      due: (board.reminders.data.due ?? []).filter((r) => !r.done).length,
      nextActions: board.defs.data.goals.filter((g) => g.status === 'active' && g.nextAction).length,
      habits: board.defs.data.habits.length,
    },
  });

  if (modeOn) return renderModeOn(o, spotlight) + overlays(o);

  const views = directionViews(board, { suppressConfrontations: modeOn, now: o.now });

  return `<div class="vH ${spotlight.source === 'compose' ? 'hostage' : ''}">
    ${renderStrip(views)}
    <div class="main">
      ${renderStage({
        spotlight,
        board,
        now: o.now,
        modeOn,
        modePending: o.ui.pending.has('mode'),
        canCompose: o.canCompose,
      })}
      ${renderFolds({ board, ui: o.ui, now: o.now, spotlightRef: spotlight.refId })}
    </div>
  </div>${overlays(o)}`;
}

/**
 * Mode-on: the board strips to the Spotlight plus elapsed focus time; the
 * cascade fades and no new confrontation surfaces until Mode ends
 * (ticket 02). The one check-off stays — ticking the Now item is what
 * advances it.
 */
function renderModeOn(o: RenderOptions, spotlight: Spotlight): string {
  const mode = o.state!.data.state.data!.mode;
  const since = mode.since ? Date.parse(mode.since) : NaN;
  const secs = Number.isFinite(since) ? (o.now.getTime() - since) / 1000 : mode.elapsedS;
  const key = spotlight.source === 'planItem' ? `planItem:${spotlight.refId}` : '';
  return `<div class="mode-screen">
    <div class="k">Mode on · distractions blocked</div>
    <div class="elapsed num" data-elapsed>${elapsed(secs)}</div>
    <div class="what">${esc(spotlight.what)}</div>
    <div class="serves">${esc(spotlight.serves)}</div>
    ${
      spotlight.source === 'planItem' && spotlight.refId
        ? `<div class="mode-check">${checkbox('planItem', spotlight.refId, false, {
            pending: o.ui.pending.has(key),
            failed: o.ui.failed.has(key),
          })}<span>tick it and the next one steps up</span></div>`
        : ''
    }
    ${renderModeButton(true, o.ui.pending.has('mode'))}
  </div>`;
}

function overlays(o: RenderOptions): string {
  const parts: string[] = [];
  if (o.ui.disconnected) {
    parts.push(
      `<div class="banner">Write door unreachable — showing the last good render.</div>`,
    );
  }
  if (o.ui.composing && o.state && o.canCompose) {
    parts.push(renderCompose({ board: o.state.data, ui: o.ui }));
  }
  if (o.ui.flashes.length) {
    parts.push(
      `<div class="flashes">${o.ui.flashes
        .map((f) => `<div class="flash ${f.kind}">${esc(f.text)}</div>`)
        .join('')}</div>`,
    );
  }
  return parts.join('');
}
